import { useState } from "react";
import type { MouseEvent } from "react";
import { Link } from "@tanstack/react-router";
import { ChevronRight, CircleDot, Clock, RotateCcw, User } from "lucide-react";
import { OfflineImage } from "@/components/common/offline-image/offline-image";
import { IssueDatePill } from "@/components/issues/issue-date-pill/issue-date-pill";
import { IssueStatusPill } from "@/components/issues/issue-status-pill/issue-status-pill";
import { IssueStatusTrigger } from "@/components/issues/issue-status-trigger/issue-status-trigger";
import { useStatusChangePulse } from "@/hooks/use-status-change-pulse";
import { SWIPE_ACTION_WIDTH, useSwipeAction } from "@/hooks/use-swipe-action";
import { useSwipeHint } from "@/hooks/use-swipe-hint";
import {
  currentTargetDate,
  formatIssueNumber,
  overdueDays,
  overdueLabel,
  personName,
  showsIssueDates,
} from "@/lib/issues";
import { cn } from "@/lib/utils";
import type { ProjectIssue } from "@/types/api";
import { IssuePickCircle, type IssueRowSelection } from "./issue-pick-circle";
import { openIssueClick } from "./open-issue-click";

interface IssueItemProps {
  projectId: string;
  issue: ProjectIssue;
  /**
   * Open the issue in place instead of navigating to its page — the Issues
   * page's desktop side panel. Modified clicks still follow the link.
   */
  onOpen?: (issue: ProjectIssue) => void;
  /** This issue is the one open in the side panel. */
  isActive?: boolean;
  /**
   * Open this issue's update sheet from the row — the list's shortcut to the
   * issue page's Update (or, on a closed issue, Reopen) bar. Omitted wherever
   * this viewer could not post one, and by hosts that only list issues. See
   * [[issue-item.spec]] US-001 and US-002.
   */
  onUpdateStatus?: (issue: ProjectIssue) => void;
  /**
   * How the row offers it: `swipe` drags the row left to reveal the action
   * (phones — touch only); `chip` makes the status pill a button (desktop).
   */
  updateStatusVia?: "swipe" | "chip";
  /**
   * This row is the list's first swipeable one, so it plays the daily peek
   * that shows rows swipe. The list passes it to a single row; the row still
   * checks the device has not seen the hint. See [[issue-item.spec]] US-004.
   */
  swipeHint?: boolean;
  /**
   * Selection mode: the row ticks instead of opening. It leads with a pick
   * circle, drops the chevron, swipe and status button, and becomes one toggle
   * button. See [[issue-item.spec]] § Selection mode.
   */
  selection?: IssueRowSelection;
}

export function IssueItem({
  projectId,
  issue,
  onOpen,
  isActive = false,
  onUpdateStatus,
  updateStatusVia = "swipe",
  swipeHint = false,
  selection,
}: IssueItemProps) {
  // A broken image URL renders a torn-image icon, which looks like a bug in a
  // list. Fall back to the same neutral placeholder a photo-less record gets.
  const [thumbnailFailed, setThumbnailFailed] = useState(false);
  const thumbnail = thumbnailFailed ? null : issue.record_cover_thumbnail;

  const overdueCount = overdueDays(issue);
  const overdue = overdueLabel(issue);
  // List rows receive the server's newest non-null target projection. Detail
  // reads still derive it from their full trail, keeping one source of truth.
  const target = issue.target_date ?? currentTargetDate(issue.updates);
  // Done work drops its dates and its lateness — nothing left to chase or
  // forecast — the same rule as the detail heading. See [[issue-item.spec]]
  // § Behavior.
  const showDates = showsIssueDates(issue);
  // Derived, not stored: `completed` is the one state waiting on a human
  // decision rather than on work, so it is true for every viewer at once and
  // needs no per-user read state. See [[issue-item.spec]].
  const needsAttention = issue.status === "completed";
  // A closed issue's one update is a reopen; the swipe says so.
  const reopens = issue.status === "closed";

  // Selecting turns the shortcuts off: the row has one job, ticking.
  const selecting = !!selection;
  const swipeEnabled =
    !selecting && !!onUpdateStatus && updateStatusVia === "swipe";
  const chipEnabled =
    !selecting && !!onUpdateStatus && updateStatusVia === "chip";
  const {
    offset: swipeOffset,
    isDragging,
    handlers: swipeHandlers,
    consumeClick: consumeSwipeClick,
  } = useSwipeAction({
    enabled: swipeEnabled,
    onTrigger: () => onUpdateStatus?.(issue),
  });
  const { ref: hintRef, isHinting } = useSwipeHint({
    enabled: swipeHint && swipeEnabled,
    list: "issues",
  });
  // The pill settles in when a refetch hands this row a new status — typically
  // right after its update posts. See [[issue-item.spec]] US-005.
  const statusPulsing = useStatusChangePulse(issue.status);

  const openInPlace = openIssueClick(issue, onOpen);
  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    // Swallow the click that trails a swipe — the drag already opened the
    // sheet, and following the link too would navigate out from under it.
    if (consumeSwipeClick()) {
      event.preventDefault();
      return;
    }
    openInPlace?.(event);
  };

  const content = (
    <>
      {thumbnail ? (
        // Same anonymous-CORS mode as the evidence strip and photo viewer,
        // which load this S3 thumbnail too; a no-CORS load here would poison
        // their cache entry. See [[s3-image-fetching]].
        <OfflineImage
          assetKey=""
          src={thumbnail}
          alt=""
          loading="lazy"
          onError={() => setThumbnailFailed(true)}
          className="h-16 w-16 shrink-0 rounded-md object-cover"
        />
      ) : (
        <div
          className="h-16 w-16 shrink-0 rounded-md bg-muted"
          aria-hidden="true"
        />
      )}

      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        <span className="truncate text-sm font-medium tracking-[-0.005em]">
          {formatIssueNumber(issue.number)} &mdash; {issue.title}
        </span>
        {/* Wraps rather than clips: on a phone the dates drop to a second line
            instead of disappearing off the edge. */}
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span
            className={cn(
              "inline-flex shrink-0 items-center gap-0.5 whitespace-nowrap text-[11px]",
              issue.assigned_to ? "text-muted-foreground" : "text-muted-foreground/70",
            )}
          >
            <User className="h-2.5 w-2.5" aria-hidden="true" />
            {issue.assigned_to ? personName(issue.assigned_to) : "Unassigned"}
          </span>
          {/* Small chips: the meta line is a row of secondary facts under the
              title, so the pills sit at the size of the type around them
              rather than standing off it. See [[issue-item.spec]]. */}
          <span
            className={cn(
              "inline-flex shrink-0",
              statusPulsing && "status-pill-pulse",
            )}
          >
            {chipEnabled ? (
              <IssueStatusTrigger
                status={issue.status}
                size="sm"
                onClick={() => onUpdateStatus?.(issue)}
              />
            ) : (
              <IssueStatusPill status={issue.status} size="sm" />
            )}
          </span>
          {showDates && (
            <>
              {/* Target first, then the deadline it is measured against, then
                  how late that deadline is. Both pills are tinted; the due one
                  turns the day count's red when overdue. */}
              <IssueDatePill
                kind="target"
                value={target}
                size="sm"
                shortOnMobile
                tinted
              />
              <IssueDatePill
                kind="due"
                value={issue.due_date}
                size="sm"
                shortOnMobile
                tinted
                overdue={overdueCount != null}
              />
            </>
          )}
          {/* Lateness is a glance, not a sentence: a clock and the day count
              in the overdue red — no fill, no outline, so it does not read as
              a third chip beside the status and date pills. A deep brick red,
              not the destructive red of errors, and only on the count and the
              due pill, so a list with many late issues is not a wall of red.
              The full phrase stays in the accessible name. */}
          {showDates && overdueCount != null && overdue && (
            <span
              aria-label={overdue}
              className="inline-flex shrink-0 items-center gap-0.5 whitespace-nowrap text-[10px] font-medium text-overdue"
            >
              <Clock className="h-2.5 w-2.5" aria-hidden="true" />
              <span aria-hidden="true">{overdueCount}d</span>
            </span>
          )}
        </div>
      </div>

      {needsAttention && (
        <span
          className="h-1.5 w-1.5 shrink-0 rounded-full bg-tertiary"
          aria-label="Waiting to be accepted"
        />
      )}
    </>
  );

  if (selection) {
    // One toggle button, not a link: a tap, Enter or Space ticks it and nothing
    // navigates, not even a modified click. Ticked rows take the light primary
    // fill rather than the muted grey the open issue uses — that grey is the
    // Pending pill's own fill, so a ticked Pending row would lose its status.
    return (
      <button
        type="button"
        onClick={selection.onToggle}
        aria-pressed={selection.selected}
        className={cn(
          "flex w-full items-center gap-3 p-1.5 text-left transition-colors duration-100",
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
          selection.selected
            ? "bg-primary/10"
            : "bg-card hover:bg-card-hover active:bg-card-pressed",
        )}
      >
        <IssuePickCircle selected={selection.selected} className="ml-1.5" />
        {content}
      </button>
    );
  }

  // The row carries no border or radius of its own: [[issues-view.spec]] owns
  // the rounded, bordered container and the hairline between rows, so the list
  // reads as one block. Press feedback is a background change rather than a
  // scale, which would drag the shared divider with it.
  const row = (
    <Link
      ref={hintRef}
      to="/projects/$projectId/issues/$issueId"
      params={{ projectId, issueId: issue.id }}
      onClick={handleClick}
      {...(swipeEnabled ? swipeHandlers : {})}
      aria-current={isActive || undefined}
      style={
        swipeEnabled
          ? {
              // Vertical scrolling stays with the list; horizontal is ours.
              touchAction: "pan-y",
              transform: swipeOffset
                ? `translateX(${swipeOffset}px)`
                : undefined,
            }
          : undefined
      }
      className={cn(
        "flex items-center gap-3 bg-card p-1.5 active:bg-card-pressed",
        // Springs back after a release; follows the finger during the drag.
        swipeEnabled && !isDragging
          ? "transition-[background-color,transform] duration-200"
          : "transition-colors duration-100",
        isHinting && "swipe-hint-nudge",
        // The issue open in the side panel: the same muted fill and tertiary
        // left edge as the open record in Photos list view.
        isActive
          ? "bg-muted shadow-[inset_3px_0_0_var(--tertiary)]"
          : // Same hover as the wide table row. Tailwind's hover variant only
            // applies on devices that can hover, so a tap on a phone doesn't
            // leave a row stuck tinted.
            "hover:bg-card-hover",
      )}
    >
      {content}
      <ChevronRight
        className="h-4 w-4 shrink-0 text-muted-foreground"
        aria-hidden="true"
      />
    </Link>
  );

  if (!swipeEnabled) return row;

  // The swipe needs a frame to clip the sliding row and hold the action behind
  // it. Only added when the row can swipe, so every other host keeps the bare
  // link it has always had.
  return (
    <div className="relative overflow-hidden bg-card">
      {/* Decorative while closed — the gesture commits it, so it takes no tap
          of its own. Same treatment as the photos list's "Raise issue", and
          what the peek hint shows a sliver of. */}
      {(swipeOffset < 0 || isHinting) && (
        <div
          aria-hidden="true"
          data-testid="swipe-update-status"
          // Icon over label: "Update status" is too long to sit beside its
          // glyph in the photos row's width, and the issue row is tall enough
          // to stack them.
          className="absolute inset-y-0 right-0 flex flex-col items-center justify-center gap-1 whitespace-nowrap bg-tertiary px-2 text-xs font-medium text-tertiary-foreground"
          style={{ width: SWIPE_ACTION_WIDTH }}
        >
          {reopens ? (
            <RotateCcw className="h-4 w-4 shrink-0" />
          ) : (
            <CircleDot className="h-4 w-4 shrink-0" />
          )}
          {reopens ? "Reopen" : "Update status"}
        </div>
      )}
      {row}
    </div>
  );
}
